import { useEffect, useRef, useState } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

type Props = {
  name: string;
  value: number;
  defaultValue: number;
  onSave: (next: number) => Promise<unknown> | unknown;
};

export function RuleParamInput({ name, value, defaultValue, onSave }: Props) {
  const [draft, setDraft] = useState(String(value));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const dirty = String(value) !== draft.trim();
  const canReset = value !== defaultValue;

  const commit = async (raw: string) => {
    const next = Number(raw);
    if (!Number.isFinite(next)) {
      setDraft(String(value));
      return;
    }
    if (next === value) return;
    setSaving(true);
    try {
      await onSave(next);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1100);
      toast.success(`${name} → ${next}`);
    } catch {
      toast.error(`Failed to update ${name}`);
      setDraft(String(value));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!canReset) return;
    setDraft(String(defaultValue));
    void commit(String(defaultValue));
  };

  return (
    <label
      className={cn(
        'group relative flex items-center gap-2 px-2 py-1 rounded border bg-zinc-900/40 transition-colors',
        savedFlash
          ? 'border-emerald-500/60'
          : dirty
            ? 'border-indigo-500/60'
            : 'border-zinc-800 hover:border-zinc-700',
      )}
      onClick={() => ref.current?.focus()}
    >
      <span className="text-zinc-400 text-xs">{name}</span>
      <input
        ref={ref}
        type="number"
        inputMode="decimal"
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => void commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setDraft(String(value));
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="bg-transparent outline-none w-16 text-right num text-sm focus:text-indigo-100"
      />
      {savedFlash ? (
        <Check className="size-3 text-emerald-400" />
      ) : canReset ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            reset();
          }}
          title={`Reset to default (${defaultValue})`}
          aria-label={`Reset ${name} to default (${defaultValue})`}
          className="text-zinc-400 hover:text-zinc-200 p-1"
        >
          <RotateCcw className="size-3" />
        </button>
      ) : (
        <span className="size-3" />
      )}
    </label>
  );
}
